import type { ConversationLayoutNode } from '@/lib/conversations/conversation-layout';
import { DEFAULT_PANEL_SIZE_PERCENT } from '@/lib/side-panel/panel-resize';
import { NO_PANEL_TABS, type PanelTab, type PanelTabs } from '@/lib/side-panel/panel-tabs';
import type { SidePanelView } from '@/lib/side-panel/side-panel-items';

/**
 * What an app tab shows: the item selected in its sidebar, the section (`page.sidebars` id) it is
 * of, and the route of its view.
 */
export type AppPanelTarget = {
  item: string | null;
  section?: string | undefined;
  route?: string | undefined;
};

/**
 * What a conversation's side panel shows. Every conversation keeps its own: the shown one lives
 * in the store's side panel fields, the others are parked in `panelLayouts` until shown again.
 */
export type PanelLayout = {
  open: boolean;
  tabs: PanelTabs;
  size: number;
  /** The size before it was maximized, restored when it no longer is. */
  restoreSize: number;
  appTargets: Readonly<Record<string, AppPanelTarget>>;
  selectedSubagentId: string | null;
  messageReviewDiff: string | null;
  /** The tabs shown side by side in the panel (see `panel-split.ts`); null when none are. */
  split: ConversationLayoutNode | null;
};

/** The store fields holding the shown conversation's panel. */
export type ShownPanelFields = {
  sidePanelOpen: boolean;
  panelTabs: PanelTabs;
  panelSize: number;
  panelRestoreSize: number;
  appPanelTargets: Readonly<Record<string, AppPanelTarget>>;
  selectedSubagentId: string | null;
  messageReviewDiff: string | null;
  panelSplit: ConversationLayoutNode | null;
};

/** A conversation's panel before anything was opened in it: closed, at the width last used. */
export function emptyPanelLayout(width: number = DEFAULT_PANEL_SIZE_PERCENT): PanelLayout {
  return {
    open: false,
    tabs: NO_PANEL_TABS,
    size: width,
    restoreSize: width,
    appTargets: {},
    selectedSubagentId: null,
    messageReviewDiff: null,
    split: null,
  };
}

export function panelLayoutOf(fields: ShownPanelFields): PanelLayout {
  return {
    open: fields.sidePanelOpen,
    tabs: fields.panelTabs,
    size: fields.panelSize,
    restoreSize: fields.panelRestoreSize,
    appTargets: fields.appPanelTargets,
    selectedSubagentId: fields.selectedSubagentId,
    messageReviewDiff: fields.messageReviewDiff,
    split: fields.panelSplit,
  };
}

export function shownPanelFields(layout: PanelLayout): ShownPanelFields {
  return {
    sidePanelOpen: layout.open,
    panelTabs: layout.tabs,
    panelSize: layout.size,
    panelRestoreSize: layout.restoreSize,
    appPanelTargets: layout.appTargets,
    selectedSubagentId: layout.selectedSubagentId,
    messageReviewDiff: layout.messageReviewDiff,
    panelSplit: layout.split,
  };
}

/**
 * Shows `nextId`'s panel: parks the shown conversation's under `currentId` and takes out the
 * next one's, or an empty panel at the current width when it never had one.
 */
export function switchPanelLayout(
  state: ShownPanelFields & { panelLayouts: Readonly<Record<string, PanelLayout>> },
  currentId: string,
  nextId: string,
): ShownPanelFields & { panelLayouts: Readonly<Record<string, PanelLayout>> } {
  const { [nextId]: next, ...parked } = state.panelLayouts;
  const width = state.panelSize >= 100 ? state.panelRestoreSize : state.panelSize;
  return {
    ...shownPanelFields(next ?? emptyPanelLayout(width)),
    panelLayouts: { ...parked, [currentId]: panelLayoutOf(state) },
  };
}

/** The ids of the tabs open in the panels of the conversations not shown. */
export function parkedPanelTabIds(layouts: Readonly<Record<string, PanelLayout>>): Set<string> {
  return new Set(Object.values(layouts).flatMap((layout) => layout.tabs.tabs.map((tab) => tab.id)));
}

const STORAGE_KEY = 'crowdex.desktop.panel-layouts';
/** How many conversations' panels are remembered across reloads (the most recently shown). */
const STORED_LAYOUTS = 100;

/** The panels remembered across reloads; transient content (a message's diff) is not kept. */
export function readStoredPanelLayouts(
  storage: Pick<Storage, 'getItem'> = localStorage,
): Record<string, PanelLayout> {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(STORAGE_KEY) ?? '{}');
    if (typeof parsed !== 'object' || parsed === null) return {};
    const layouts: Record<string, PanelLayout> = {};
    for (const [id, value] of Object.entries(parsed)) {
      const layout = parseStoredLayout(value);
      if (layout !== null) layouts[id] = layout;
    }
    return layouts;
  } catch {
    return {};
  }
}

export function writeStoredPanelLayouts(
  layouts: Readonly<Record<string, PanelLayout>>,
  storage: Pick<Storage, 'setItem'> = localStorage,
): void {
  const kept = Object.entries(layouts)
    .slice(-STORED_LAYOUTS)
    .map(([id, layout]) => [id, { ...layout, messageReviewDiff: null }] as const);
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(kept)));
  } catch {
    // A full storage only loses the panels remembered across reloads.
  }
}

function parseStoredLayout(value: unknown): PanelLayout | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  const size = record['size'];
  const restoreSize = record['restoreSize'];
  const tabs = parseStoredTabs(record['tabs']);
  if (typeof size !== 'number' || typeof restoreSize !== 'number' || tabs === null) return null;
  const appTargets = record['appTargets'];
  const selectedSubagentId = record['selectedSubagentId'];
  return {
    open: record['open'] === true,
    tabs,
    size,
    restoreSize,
    appTargets:
      typeof appTargets === 'object' && appTargets !== null
        ? (appTargets as Record<string, AppPanelTarget>)
        : {},
    selectedSubagentId: typeof selectedSubagentId === 'string' ? selectedSubagentId : null,
    messageReviewDiff: null,
    split: parseStoredSplit(record['split']),
  };
}

function parseStoredTabs(value: unknown): PanelTabs | null {
  if (typeof value !== 'object' || value === null) return null;
  const record = value as Record<string, unknown>;
  const tabs = record['tabs'];
  const recent = record['recent'];
  const activeId = record['activeId'];
  if (!Array.isArray(tabs) || !Array.isArray(recent)) return null;
  const parsed = tabs.flatMap((tab): PanelTab[] => {
    if (typeof tab !== 'object' || tab === null) return [];
    const { id, view } = tab as Record<string, unknown>;
    return typeof id === 'string' && typeof view === 'string'
      ? [{ id, view: view as SidePanelView }]
      : [];
  });
  return {
    tabs: parsed,
    activeId:
      typeof activeId === 'string' && parsed.some((tab) => tab.id === activeId) ? activeId : null,
    recent: recent.filter(
      (id): id is string => typeof id === 'string' && parsed.some((tab) => tab.id === id),
    ),
  };
}

function parseStoredSplit(value: unknown): ConversationLayoutNode | null {
  if (typeof value !== 'object' || value === null) return null;
  const node = value as Record<string, unknown>;
  const id = node['id'];
  if (node['kind'] === 'pane') return typeof id === 'string' ? { kind: 'pane', id } : null;
  const orientation = node['orientation'];
  const first = parseStoredSplit(node['first']);
  const second = parseStoredSplit(node['second']);
  if (
    node['kind'] !== 'split' ||
    typeof id !== 'string' ||
    (orientation !== 'horizontal' && orientation !== 'vertical') ||
    first === null ||
    second === null
  )
    return null;
  return { kind: 'split', id, orientation, first, second };
}
